import { ForwardedRef, forwardRef, Fragment, useCallback, useMemo } from 'react';
import { Calendar } from '@components/calendar';
import { IconCross } from '@components/icons';
import { SupportedLanguages } from '@constants/constants.type';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { getFnsLocale } from '@hooks/use-get-locale/use-get-locale';
import classNames from 'classnames';
import moment from 'moment-timezone';

import { type DatePickerProps } from './date-picker.type';

// Convert a moment to a browser-local Date preserving the displayed day.
// DayPicker uses browser-local dates internally, so we extract year/month/day
// from the moment's timezone display to ensure the correct day is highlighted.
const momentToDate = (m: moment.Moment | null | undefined): Date | undefined => {
  if (!m) return undefined;
  return new Date(m.year(), m.month(), m.date());
};

export const DatePicker = forwardRef(
  (
    {
      value,
      onChange,
      placeholder,
      minBookingDate,
      maxBookingDate,
      initialVisibleMonth,
      hasError,
      locale,
      showClearDate,
      disabled,
      readOnly,
      id,
      name,
      timezone,
      'data-testid': dataTestId,
    }: DatePickerProps,
    _: ForwardedRef<HTMLInputElement>
  ) => {
    const dateFnsLocale = useMemo(() => getFnsLocale((locale ?? 'en') as SupportedLanguages), [locale]);

    const minDate = useMemo(() => momentToDate(minBookingDate), [minBookingDate]);
    const maxDate = useMemo(() => momentToDate(maxBookingDate), [maxBookingDate]);
    const selectedDate = useMemo(() => momentToDate(value), [value]);

    const defaultMonth = useMemo(() => {
      if (initialVisibleMonth) return momentToDate(initialVisibleMonth());
      if (minBookingDate) return momentToDate(minBookingDate);
      // When a value is set, open the calendar on that month (not the current system month).
      if (value) return momentToDate(value);
      return undefined;
    }, [initialVisibleMonth, minBookingDate, value]);

    const displayValue = useMemo(() => {
      if (!value) return '';
      const format = moment.localeData(locale ?? 'es').longDateFormat('LL');
      return value.format(format);
    }, [value, locale]);

    const handleSelect = useCallback(
      (date: Date | undefined) => {
        if (!date) {
          onChange?.(null);
          return;
        }
        // Extract the visual day the user clicked (year/month/day from the browser-local Date).
        // Create the moment at midnight in the target timezone so the date is always
        // interpreted in the venue/event timezone, not the browser timezone.
        const tz = timezone ?? value?.tz?.();
        if (tz) {
          onChange?.(moment.tz({ year: date.getFullYear(), month: date.getMonth(), date: date.getDate() }, tz));
        } else {
          onChange?.(moment(date));
        }
      },
      [onChange, value, timezone]
    );

    const handleClear = useCallback(() => {
      onChange?.(null);
    }, [onChange]);

    const isInteractive = !disabled && !readOnly;
    const showClear = !!(showClearDate && value && isInteractive);

    return (
      <Popover as={Fragment}>
        <div
          className={classNames(
            'relative flex w-full items-center text-body-m rounded-md border p-3 pl-2 fill-primary',
            {
              'border-critical-no-bg hover:border-critical-no-bg': hasError,
              'border-soft hover:border-hard': !hasError && isInteractive,
              'cursor-not-allowed border-soft': !isInteractive,
              'cursor-pointer': isInteractive,
            }
          )}>
          <span
            aria-hidden='true'
            className={classNames('block truncate flex-1', {
              'text-primary': isInteractive && !!value,
              'text-tertiary': !isInteractive || !value,
              'pr-6': showClear,
            })}>
            {displayValue || placeholder}
          </span>
          <PopoverButton
            id={id}
            name={name}
            disabled={!isInteractive}
            data-testid={dataTestId}
            aria-label={displayValue || placeholder}
            className='absolute inset-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-hard'
          />
          {showClear && (
            <button
              type='button'
              onClick={handleClear}
              className='relative z-10 ml-auto p-1 -mr-2'
              aria-label='Clear date'>
              <IconCross className='w-3 h-3 text-primary' />
            </button>
          )}
        </div>

        {isInteractive && (
          <PopoverPanel
            anchor='bottom start'
            className='z-50 rounded-lg shadow-calendar bg-surface-primary mt-1 scrollbar-xceed'>
            {({ close }) => (
              <Calendar
                mode='single'
                selected={selectedDate}
                onSelect={(date) => {
                  handleSelect(date);
                  close();
                }}
                locale={dateFnsLocale}
                weekStartsOn={dateFnsLocale?.options?.weekStartsOn as 0 | 1 | 2 | 3 | 4 | 5 | 6}
                minDate={minDate}
                maxDate={maxDate}
                defaultMonth={defaultMonth}
              />
            )}
          </PopoverPanel>
        )}
      </Popover>
    );
  }
);
